import React from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import OrderService from '../services/OrderService';
import { useAuthMonitor } from '../hooks/useAuthMonitor';
import { notifyTokenObserved, installTokenEndpointWatcher } from './tokenExpiry';

type Props = { children: React.ReactNode };

function Gate({ children, audience }: { children: React.ReactNode; audience?: string }) {
  const { isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently, error } = useAuth0();
  useAuthMonitor();

  const decodeJwt = (token: string) => {
    try {
      const [h, p] = token.split('.');
      const base64UrlToJson = (s: string) =>
        JSON.parse(
          decodeURIComponent(
            atob(s.replace(/-/g, '+').replace(/_/g, '/'))
              .split('')
              .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          )
        );
      return {
        header: base64UrlToJson(h),
        payload: base64UrlToJson(p)
      };
    } catch (e) {
      try { console.warn('[SSO] Failed to decode JWT payload', e); } catch {}
      return null;
    }
  };

  React.useEffect(() => {
    // Watch the OAuth token endpoint so a 403 on refresh forces a logout.
    installTokenEndpointWatcher();
    OrderService.setGlobalAuthTokenProvider(async () => {
      const token = await getAccessTokenSilently(
        audience
          ? { authorizationParams: { audience } }
          : undefined
      );
      const t = (token || '').trim() || null;
      // Let the session monitor re-arm its expiry logout against this (possibly
      // refreshed) token.
      notifyTokenObserved(t);
      return t;
    });
  }, [getAccessTokenSilently, audience]);

  React.useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: window.location.pathname + window.location.search }
      }).catch(() => {});
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  if (isLoading) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'40vh', color:'var(--text-primary)' }}>
        Signing in…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding:16, color:'var(--color-error)' }}>
        Auth error: {String(error?.message || error)}
      </div>
    );
  }
  if (!isAuthenticated) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'40vh', color:'var(--text-primary)' }}>
        Signing in…
      </div>
    );
  }
  return <>{children}</>;
}

export default function Auth0ProviderWithConfig({ children }: Props) {
  const domain = String(import.meta.env.VITE_AUTH0_DOMAIN || '').trim();
  const clientId = String(import.meta.env.VITE_AUTH0_CLIENT_ID || '').trim();
  const audience = String(import.meta.env.VITE_AUTH0_AUDIENCE || '').trim();
  const useSSO = !!domain && !!clientId;

  if (!useSSO) {
    return <>{children}</>;
  }

  const redirectUri = (() => {
    const explicit = String(import.meta.env.VITE_AUTH0_CALLBACK_URL || '').trim();
    if (explicit) return explicit;
    if (typeof window !== 'undefined' && window.location) {
      return `${window.location.origin}/callback`;
    }
    return undefined;
  })();

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        ...(audience ? { audience } : {}),
        scope: 'openid profile email'
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <Gate audience={audience}>{children}</Gate>
    </Auth0Provider>
  );
}


